import React, { useEffect, useRef, useState } from "react";
import { Typography, Box, CircularProgress } from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";
import { useCaptchaChallenge } from "../../hooks/useCaptchaChallenge";
import { FormActionButton } from "../common/FormActionButton";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { updateUserPassword } from "../../redux/actions/userActions";
import { EmailInputField } from "../common/InputFields/EmailInputField";
import { PasswordInputField } from "../common/InputFields/PasswordInputField";
import { ErrorMessage } from "../common/ErrorMessage";
import SnackbarAlert, { SnackbarRef } from "../common/SnackbarAlert";

interface ISettingsProps {
  userEmail: string;
}

export const Settings: React.FC<ISettingsProps> = ({ userEmail }) => {
  const { recaptchaRef, captchaError, withCaptcha } = useCaptchaChallenge();
  const formRef = useRef<HTMLFormElement>(null);
  const snackbarRef = useRef<SnackbarRef>(null);
  const [isPasswordUpdated, setIsPasswordUpdated] = useState(false);
  const formMethods = useForm<{
    email: string;
    currentPassword: string;
    newPassword: string;
  }>({
    mode: "onBlur",
    defaultValues: { email: userEmail, currentPassword: "", newPassword: "" },
  });
  const { handleSubmit, reset, getValues, setValue } = formMethods;

  const dispatch = useDispatch();

  const { loading, error } = useSelector((state: RootState) => state.users);

  const onSubmit: SubmitHandler<{
    email: string;
    currentPassword: string;
    newPassword: string;
  }> = async (formData) => {
    setIsPasswordUpdated(false);
    const success = await withCaptcha((token) =>
      updateUserPassword(
        formData.email,
        formData.currentPassword,
        formData.newPassword,
        token,
      )(dispatch),
    );
    setIsPasswordUpdated(success);
  };

  useEffect(() => {
    if (!loading && !error && isPasswordUpdated) {
      setIsPasswordUpdated(false);
      snackbarRef.current?.showSnackbar(
        "Password Updated Successfully",
        "success",
      );
      reset({
        email: userEmail,
        currentPassword: "",
        newPassword: "",
      });
    }
  }, [loading, error, isPasswordUpdated, reset, userEmail]);

  useEffect(() => {
    setValue("email", userEmail);
  }, [userEmail, setValue]);

  return (
    <Box>
      <SnackbarAlert ref={snackbarRef} />
      <Typography variant="h6" fontWeight="bold" mb={2}>
        My Settings
      </Typography>

      <Typography variant="body1" mb={5}>
        To update your account password, please provide your current and new
        password below. Make sure your new password meets the required criteria.
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
        }}
      >
        <FormProvider {...formMethods}>
          <form
            ref={formRef}
            noValidate
            style={{ width: "100%", maxWidth: "500px" }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <fieldset
              disabled={loading || formMethods.formState.isSubmitting}
              style={{ border: 0, padding: 0 }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <EmailInputField
                  fieldName="email"
                  required
                  sx={{ mb: "12px" }}
                  fieldLabel="Email Address"
                  isDisabled={true}
                />
                <PasswordInputField
                  fieldName="currentPassword"
                  required
                  fieldLabel="Current Password"
                  sx={{ mb: "12px" }}
                />
                <PasswordInputField
                  fieldName="newPassword"
                  required
                  fieldLabel="New Password"
                  sx={{ mb: "12px" }}
                  rules={{
                    validate: (value) => {
                      if (value === getValues("currentPassword")) {
                        return "Your new password must be different from your current password. Please choose a new one.";
                      }
                      return true;
                    },
                  }}
                />
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY || ""}
                  size="invisible"
                />
                <FormActionButton
                  text={
                    loading ? <CircularProgress size={20} /> : "Update Password"
                  }
                  onClick={() => {}}
                />
                {(captchaError || error) && (
                  <ErrorMessage text={captchaError || error || ""} />
                )}
              </Box>
            </fieldset>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
};
